import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Pencil, Trash2, Users, Tag, Percent } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type Group = {
  id: number;
  name: string;
  discountType: string;
  discountValue: number;
  color: string;
  memberCount: number;
};

const PRESET_COLORS = [
  "#6366f1", "#8b5cf6", "#ec4899", "#f43f5e",
  "#f97316", "#eab308", "#22c55e", "#14b8a6",
  "#0ea5e9", "#3b82f6", "#64748b",
];

const EMPTY: Partial<Group> = { name: "", discountType: "percentage", discountValue: 0, color: "#6366f1" };

export default function CustomerGroups() {
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: groups = [], isLoading } = useQuery<Group[]>({
    queryKey: ["customer-groups"],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/customer-groups`, { credentials: "include" });
      return r.json();
    },
  });

  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<Group>>(EMPTY);
  const [isEdit, setIsEdit] = useState(false);

  const refetch = () => qc.invalidateQueries({ queryKey: ["customer-groups"] });

  const save = useMutation({
    mutationFn: async (data: Partial<Group>) => {
      const url = isEdit ? `${BASE}/api/customer-groups/${data.id}` : `${BASE}/api/customer-groups`;
      const r = await fetch(url, {
        method: isEdit ? "PATCH" : "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!r.ok) throw new Error("Failed");
      return r.json();
    },
    onSuccess: () => { refetch(); setIsOpen(false); setEditing(EMPTY); },
    onError: () => toast({ title: "Failed to save group", variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: async (id: number) => {
      await fetch(`${BASE}/api/customer-groups/${id}`, { method: "DELETE", credentials: "include" });
    },
    onSuccess: () => refetch(),
    onError: () => toast({ title: "Failed to delete group", variant: "destructive" }),
  });

  const openNew = () => { setEditing(EMPTY); setIsEdit(false); setIsOpen(true); };
  const openEdit = (g: Group) => { setEditing({ ...g }); setIsEdit(true); setIsOpen(true); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Customer Groups</h1>
          <p className="text-muted-foreground mt-1">
            Group customers and apply automatic discounts at the POS.
          </p>
        </div>
        <Dialog open={isOpen} onOpenChange={o => { setIsOpen(o); if (!o) setEditing(EMPTY); }}>
          <DialogTrigger asChild>
            <Button onClick={openNew}><Plus className="w-4 h-4 mr-2" /> New Group</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{isEdit ? "Edit Group" : "New Customer Group"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-1">
              <div>
                <label className="text-sm font-medium">Group name <span className="text-destructive">*</span></label>
                <Input
                  className="mt-1"
                  placeholder="e.g. Wholesale, VIP, Reseller"
                  value={editing.name ?? ""}
                  onChange={e => setEditing(p => ({ ...p, name: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Discount type</label>
                  <Select
                    value={editing.discountType ?? "percentage"}
                    onValueChange={v => setEditing(p => ({ ...p, discountType: v }))}
                  >
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage"><span className="flex items-center gap-1.5"><Percent className="w-3.5 h-3.5" /> Percentage (%)</span></SelectItem>
                      <SelectItem value="fixed"><span className="flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Fixed amount (₦)</span></SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">
                    {editing.discountType === "fixed" ? "Amount (₦)" : "Discount (%)"}
                  </label>
                  <Input
                    className="mt-1"
                    type="number"
                    min={0}
                    max={editing.discountType === "percentage" ? 100 : undefined}
                    step={editing.discountType === "percentage" ? 1 : 100}
                    value={editing.discountValue ?? 0}
                    onChange={e => setEditing(p => ({ ...p, discountValue: Number(e.target.value) }))}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Label colour</label>
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.map(c => (
                    <button
                      key={c}
                      type="button"
                      className={`w-7 h-7 rounded-full border-2 transition-transform ${editing.color === c ? "border-foreground scale-110" : "border-transparent"}`}
                      style={{ backgroundColor: c }}
                      onClick={() => setEditing(p => ({ ...p, color: c }))}
                    />
                  ))}
                </div>
              </div>

              <div className="bg-muted rounded-lg px-4 py-3 text-sm text-muted-foreground">
                Customers in this group will automatically get a{" "}
                <strong>
                  {editing.discountType === "fixed"
                    ? `₦${Number(editing.discountValue ?? 0).toLocaleString()} off`
                    : `${editing.discountValue ?? 0}%`}
                </strong>{" "}
                discount applied at the POS when they are linked to a sale.
              </div>

              <Button
                className="w-full"
                disabled={save.isPending || !editing.name}
                onClick={() => save.mutate(editing)}
              >
                {save.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {isEdit ? "Save changes" : "Create group"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Groups table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Group</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Members</TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></TableCell></TableRow>
            ) : groups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-14 text-muted-foreground">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No customer groups yet.</p>
                  <p className="text-xs mt-1">Create a group like "Wholesale" or "VIP" to auto-apply discounts at the POS.</p>
                </TableCell>
              </TableRow>
            ) : groups.map(g => (
              <TableRow key={g.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
                    <span className="font-medium">{g.name}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary" className="gap-1">
                    {g.discountType === "fixed"
                      ? `₦${Number(g.discountValue).toLocaleString()} off`
                      : `${g.discountValue}% off`}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className="text-muted-foreground text-sm">{g.memberCount} customer{g.memberCount !== 1 ? "s" : ""}</span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 justify-end">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(g)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => { if (confirm(`Delete "${g.name}"?`)) remove.mutate(g.id); }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {groups.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Assign customers to groups from the <strong>Customers</strong> page. Discounts apply automatically when the customer is selected at the POS.
        </p>
      )}
    </div>
  );
}
